import {IToken, TokenError} from "ebnf";
import * as functions from './functions/functions.main';

export type COTLangOutputElement = { key: string, name: string, result: any };
export type COTLangOutput = COTLangOutputElement[];
export type COTLangValue = any;

export abstract class COTLangExec {
    baseURL: string | null;
    authorization: string | null;
    environment: { [key: string]: string };

    constructor(environment: { [key: string]: string }, baseURL: string | null, authorization: string | null) {
        this.environment = environment;
        this.baseURL = baseURL;
        this.authorization = authorization;
    };

    async exec(ast: IToken, value: COTLangValue, outputs: COTLangOutput, pipeContext?: any): Promise<any> {
        try {
            let functionType = ast.type;
            let result: any;

            // if function convert to `function-xyz` to avoid custom name coalition
            if (functionType === 'function') {
                const functionName = await this.exec(ast.children[0], value, outputs);
                functionType = `function-${functionName}`;
            }

            switch (functionType) {
                // COTLang Internal commands
                case 'literaltoken':
                    result = await functions._literal(this, ast);
                    break;
                case 'token':
                    result = await functions._token(this, ast);
                    break;
                case 'pipes':
                    result = await functions._pipe(this, ast, value, outputs, pipeContext);
                    break;
                // COTLang Commands $a#b
                case 'join':
                    result = await functions.$join(this, ast, value, outputs, pipeContext);
                    break;
                case 'code':
                    result = await functions.$code(this, ast, value, outputs, pipeContext);
                    break;
                case 'value':
                    result = await functions.$value(this, ast, value, outputs, pipeContext);
                    break;
                case 'output':
                    result = await functions.$output(this, ast, value, outputs, pipeContext);
                    break;
                case 'time':
                    result = await functions.$$time(this, ast, value, outputs, pipeContext);
                    break;
                case 'data':
                    result = await functions.$data(this, ast, value, outputs, pipeContext);
                    break;
                case 'env':
                    result = await functions.$env(this, ast, value, outputs, pipeContext);
                    break;
                // COTLang Functions [a=>b=c]
                case 'function-size':
                    result = await functions.size(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-toString':
                    result = await functions.toString(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-cast':
                    result = await functions.cast(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-map':
                    result = await functions.map(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-arrayToObject':
                    result = await functions.arrayToObject(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-isDefinedFilter':
                    result = await functions.isDefinedFilter(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-filter':
                    result = await functions.filter(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-filterIndex':
                    result = await functions.filterIndex(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-notEqualFilter':
                    result = await functions.notEqualFilter(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-simpleNotEqualFilter':
                    result = await functions.simpleNotEqualFilter(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-simpleEqualFilter':
                    result = await functions.simpleEqualFilter(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-concat':
                    result = await functions.concat(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-find':
                    result = await functions.find(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-push':
                    result = await functions.push(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-add':
                    result = await functions.add(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-math':
                    result = await functions.math(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-date':
                    result = await functions.date(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-every':
                    result = await functions.every(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-some':
                    result = await functions.some(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-json':
                    result = await functions.json(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-querystring':
                    result = await functions.querystring(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-cotanswer_list':
                    result = await functions.cotanswer_list(this, ast, value, outputs, pipeContext);
                    break;
                case 'function-gencode':
                    result = await functions.gencode(this, ast, value, outputs, pipeContext);
                    break;

                // passthoughs
                case 'parenthesis':
                case 'syntax':
                case 'expression':
                case 'leaf':
                case 'command':
                case 'args':
                case 'arg':
                case 'literal':
                case 'pipe':
                    result = await functions.passthrough(this, ast, value, outputs, pipeContext);
                    break;

                default: {
                    const error = new TokenError('Unknown function: ' + functionType, ast);
                    ast.errors.push(error);
                    throw error;
                }
            }
            return result;
        } catch (e) {
            console.log('Unknown error:', e);
            throw e;
        }
    };
}
